import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ImpersonatableUsers, UserRole } from "@/services/bedrock/dtos";
import { useImpersonationDialogContext } from "../ImpersonationContext";

interface UserListItemProps {
  user: ImpersonatableUsers;
  isFocused: boolean;
  isRecent?: boolean;
}

const roleLabels: Record<UserRole, string> = {
  ADMIN: "Admin",
  EDITOR: "Editor",
  COACH: "Coach",
  ASSISTANT_COACH: "Assistant Coach",
  PLAYER: "Player",
};

const roleClasses: Record<UserRole, string> = {
  ADMIN: "bg-rose-600 text-white dark:bg-rose-800",
  EDITOR: "bg-amber-600 text-white dark:bg-amber-800",
  COACH: "bg-sky-600 text-white dark:bg-sky-800",
  ASSISTANT_COACH: "bg-cyan-600 text-white dark:bg-cyan-800",
  PLAYER: "bg-emerald-600 text-white dark:bg-emerald-800",
};

export const UserListItem: React.FC<UserListItemProps> = ({ user, isFocused, isRecent = false }) => {
  const { handleImpersonate, getInitials } = useImpersonationDialogContext();

  const fallbackColorClass = isRecent ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-600";

  return (
    <button
      data-focused={isFocused}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left relative group ${
        isFocused ? "bg-primary text-white shadow-md shadow-primary/15" : "hover:bg-slate-100 text-slate-700"
      }`}
      onClick={() => handleImpersonate(user)}
    >
      <Avatar className="h-9 w-9 border border-slate-100 shrink-0 shadow-sm">
        <Avatar.Image src={user.photoUrl} alt={user.fullName || ""} />
        <Avatar.Fallback className={`text-xs font-bold ${isFocused ? "bg-white/10 text-white" : fallbackColorClass}`}>
          {getInitials(user.fullName)}
        </Avatar.Fallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-sm font-bold truncate ${isFocused ? "text-white" : "text-slate-800"}`}>
            {user.fullName}
          </span>
          {user.userRole && (
            <Badge
              variant="default"
              size="small"
              className={cn(
                "font-medium shrink-0 text-[10px] px-1.5 py-0.5 border-none pointer-events-none shadow-none",
                isFocused
                  ? "bg-white/15 text-white"
                  : roleClasses[user.userRole] || "bg-slate-600 text-white dark:bg-slate-800"
              )}
            >
              {roleLabels[user.userRole] || user.userRole}
            </Badge>
          )}
        </div>
        <div className={`text-xs truncate ${isFocused ? "text-white/80" : "text-slate-400"}`}>{user.email}</div>
      </div>
      <ChevronRight
        className={`h-4 w-4 shrink-0 transition-transform ${
          isFocused ? "text-white translate-x-0.5" : "text-slate-300 group-hover:text-indigo-500"
        }`}
      />
    </button>
  );
};
